<!-- eslint-disable vue/v-slot-style -->
<script>
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { createNoneAgentWithTranslation } from 'dashboard/helper/agentHelper';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import ContactDetailsItem from './ContactDetailsItem.vue';
// A mesma tabela de cores do chip da lista de conversas. Importada de lá, e
// não copiada, porque as classes precisam existir num `.vue` para o Tailwind
// gerá-las — ver o comentário no `PriorityMark.vue`.
import {
  PRIORITY_CONFIG,
  PRIORITY_NONE_CONFIG,
} from 'dashboard/components/widgets/conversation/PriorityMark.vue';

const normalizeAgentOption = agent => {
  if (!agent) {
    return null;
  }

  return {
    ...agent,
    thumbnail: agent.thumbnail || agent.avatar_url || '',
  };
};

const dedupeAgentsById = agents => {
  const seenAgentIds = new Set();

  return agents.filter(agent => {
    if (!agent || seenAgentIds.has(agent.id)) {
      return false;
    }

    seenAgentIds.add(agent.id);
    return true;
  });
};

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityThumb: { width: 0, offset: 0 },
      franchiseUnits: [],
    };
  },
  computed: {
    // `computed`, e nao `data()`. O `data()` roda UMA vez, na criacao do
    // componente, entao os rotulos ficavam gravados no idioma daquele instante e
    // nunca mais mudavam. O `App.vue` aplica o locale da conta dentro de um
    // fetch assincrono (`setLocale` no `.then`): se este componente monta antes
    // da resposta chegar, os cinco nomes nascem no fallback `en-US` e a lista
    // segue em ingles com o resto da tela em portugues. O mesmo vale para a
    // troca de idioma em Configuracoes da conta, que so altera
    // `$root.$i18n.locale` -- quem ja estava montado nao acompanhava.
    // Como `computed`, o `$t` registra a dependencia do locale e a lista se
    // refaz sozinha.
    // Ordem crescente e legível (Nenhuma, Baixa, Média, Alta, Urgente) — a
    // MultiselectDropdown que este computed alimentava não se importava com a
    // ordem, mas o trilho segmentado do design é lido da esquerda para a
    // direita. Só a ordem visual mudou: os `id` continuam os mesmos valores
    // mandados para a store.
    priorityIndex() {
      return Math.max(
        this.priorityOptions.findIndex(
          option => option.id === this.assignedPriority.id
        ),
        0
      );
    },
    // A medida vem do DOM porque os segmentos têm larguras diferentes: com
    // `justify-between` não há fração de trilho que descreva onde um deles
    // começa nem quanto ele mede.
    priorityThumbStyle() {
      return {
        width: `${this.priorityThumb.width}px`,
        transform: `translateX(${this.priorityThumb.offset}px)`,
      };
    },
    // A cor acompanha o nível — e por isso o polegar também transiciona
    // `background-color`, senão a cor trocaria antes de ele chegar.
    priorityThumbClasses() {
      const config = PRIORITY_CONFIG[this.assignedPriority.id];

      // "Nenhuma" não está na tabela dos níveis, mas tem cor própria no trilho
      // (o cinza #64748b da referência) — antes ela só escurecia um degrau em
      // relação ao fundo e mal se via.
      return (config || PRIORITY_NONE_CONFIG).surface;
    },
    priorityOptions() {
      return [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
      ];
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      myTeams: 'teams/getMyTeams',
    }),
    availableAgents() {
      const assignableAgents = dedupeAgentsById(
        this.agentsList.map(normalizeAgentOption)
      ).filter(agent => agent.id !== 0);

      return [
        ...(this.assignedAgent
          ? [createNoneAgentWithTranslation(key => this.$t(key))]
          : []),
        ...assignableAgents,
      ];
    },
    // Quem decide as unidades atribuíveis é o servidor; lista vazia significa "não se aplica
    // a você aqui". O segundo termo NÃO é sobre a caixa pertencer a uma franquia: ele mantém
    // o seletor à mostra numa conversa que JÁ tem unidade, mesmo em caixa sem franquia — é
    // assim que quem recebeu um carimbo errado na própria unidade consegue desfazê-lo, e é
    // por isso que ali a única opção oferecida costuma ser "sem unidade".
    showFranchiseUnit() {
      return (
        this.franchiseUnits.length > 0 || !!this.currentChat?.franchise_unit
      );
    },
    /**
     * Nome de unidade só é único dentro da franquia, então duas "Centro" de franqueados
     * diferentes chegam iguais quando a lista atravessa franquias — o que acontece na caixa
     * de entrada sem franquia. Só nesse caso o nome do franqueado entra no rótulo; com uma
     * franquia só ele seria constante em todas as opções, e viraria ruído.
     */
    franchiseUnitsSpanFranchises() {
      const franchiseIds = new Set(
        this.franchiseUnits.map(unit => unit.franchise_id).filter(Boolean)
      );
      return franchiseIds.size > 1;
    },
    franchiseUnitsList() {
      const none = { id: 0, name: this.$t('CONVERSATION.FRANCHISE_UNIT.NONE') };
      const units = this.franchiseUnits.map(unit => ({
        ...unit,
        name: this.franchiseUnitLabel(unit),
      }));
      return this.currentChat?.franchise_unit_id ? [none, ...units] : units;
    },
    assignedFranchiseUnit: {
      get() {
        const unit = this.currentChat?.franchise_unit;
        if (!unit) return undefined;
        return { id: unit.id, name: this.franchiseUnitLabel(unit) };
      },
      set() {},
    },
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return normalizeAgentOption(this.currentChat.meta.assignee);
      },
      async set(agent) {
        const agentId = agent ? agent.id : 0;
        try {
          await this.$store.dispatch('assignAgent', {
            conversationId: this.conversationId,
            agentId,
          });
          useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
        } catch (error) {
          useAlert(
            error.response?.data?.error ||
              this.$t(
                'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.FAILED'
              )
          );
        }
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      async set(team) {
        const conversationId = this.conversationId;
        const teamId = team ? team.id : 0;
        try {
          await this.$store.dispatch('assignTeam', { conversationId, teamId });
          useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
        } catch (error) {
          useAlert(
            error.response?.data?.error ||
              this.$t(
                'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'
              )
          );
        }
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      // `priorityItem` chega NULO quando se clica no segmento que ja esta
      // valendo: o `onClickAssignPriority` trata isso como "tirar a
      // prioridade". Quem le o rotulo do alerta nao pode sair dele, entao --
      // a chamada dava certo, a prioridade mudava, e `priorityItem.name`
      // estourava um TypeError logo depois, trocando o aviso de sucesso por um
      // erro no console. `priorityOptions[0]` E a opcao "Nenhuma", que e
      // exatamente o que o nulo significa aqui.
      set(priorityItem) {
        const conversationId = this.conversationId;
        const oldValue = this.currentChat?.priority;
        const selectedOption = priorityItem || this.priorityOptions[0];
        const priority = selectedOption.id;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: selectedOption.name,
                conversationId,
              })
            );
          })
          .catch(() => {
            // A escrita local e otimista -- acontece antes de a API responder.
            // Sem este desfazer, falha de rede deixava a sidebar exibindo uma
            // prioridade que o servidor nunca aceitou, e so um reload
            // corrigia.
            this.$store.dispatch('setCurrentChatPriority', {
              priority: oldValue ?? null,
              conversationId,
            });
            useAlert(this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'));
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    isAssignedToMyTeam() {
      const assignedTeamId = this.assignedTeam?.id;
      return this.myTeams.some(
        team => Number(team.id) === Number(assignedTeamId)
      );
    },
    primaryMyTeam() {
      return this.myTeams[0];
    },
  },
  watch: {
    'currentChat.id': {
      immediate: true,
      handler() {
        this.fetchFranchiseUnits();
      },
    },
    priorityIndex() {
      this.$nextTick(this.updatePriorityThumb);
    },
    // Trocar o idioma da conta reescreve os rótulos, e rótulo novo é largura
    // nova — sem isto o polegar ficaria com a medida do idioma anterior.
    priorityOptions() {
      this.$nextTick(this.updatePriorityThumb);
    },
  },
  mounted() {
    if (this.inboxId) {
      this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
    }

    this.$nextTick(this.updatePriorityThumb);

    // A fonte carrega depois do primeiro desenho e muda a largura do texto.
    document.fonts?.ready?.then(this.updatePriorityThumb);

    // O painel muda de largura ao ser destacado, e a seção nasce escondida
    // quando a aba de contato está aberta: nos dois casos a primeira medida
    // sai zerada e só este observador avisa que já dá para medir.
    if (window.ResizeObserver && this.$refs.priorityTrack) {
      this.priorityTrackObserver = new ResizeObserver(() => {
        this.updatePriorityThumb();
      });
      this.priorityTrackObserver.observe(this.$refs.priorityTrack);
    }
  },
  beforeDestroy() {
    this.priorityTrackObserver?.disconnect();
  },
  methods: {
    updatePriorityThumb() {
      const track = this.$refs.priorityTrack;
      const button = this.$refs.priorityOptions?.[this.priorityIndex];

      if (!track || !button) {
        return;
      }

      const trackBox = track.getBoundingClientRect();
      const buttonBox = button.getBoundingClientRect();
      const width = buttonBox.width;
      const offset = buttonBox.left - trackBox.left;

      // Só grava quando muda de verdade: o observador dispara em qualquer
      // relayout, e gravar igual a cada disparo custaria um render à toa.
      if (
        this.priorityThumb.width === width &&
        this.priorityThumb.offset === offset
      ) {
        return;
      }

      this.priorityThumb = { width, offset };
    },

    /**
     * Unidade primeiro, franqueado depois — o inverso do filtro dos relatórios, e de
     * propósito: aquele é um multiselect largo, este é uma coluna estreita que corta o texto.
     * Com o franqueado na frente, "Franquia Zona Sul — Centro" vira "Franquia Zona Sul — …" e
     * some justamente o nome que se está escolhendo. Nesta ordem sobra o que importa.
     */
    franchiseUnitLabel(unit) {
      return this.franchiseUnitsSpanFranchises && unit.franchise_name
        ? `${unit.name} — ${unit.franchise_name}`
        : unit.name;
    },
    async fetchFranchiseUnits() {
      this.franchiseUnits = [];
      const conversationId = this.currentChat?.id;
      if (!conversationId) return;
      try {
        const { data } =
          await ConversationApi.assignableFranchiseUnits(conversationId);
        this.franchiseUnits = data?.franchise_units || [];
      } catch {
        // Inbox without a franchise, or no permission to pick — the selector just
        // stays hidden rather than showing an error the agent can do nothing about.
        this.franchiseUnits = [];
      }
    },
    async onClickAssignFranchiseUnit(unit) {
      const conversationId = this.currentChat?.id;
      if (!conversationId) return;
      try {
        await this.$store.dispatch('assignFranchiseUnit', {
          conversationId,
          franchiseUnitId: unit.id === 0 ? null : unit.id,
        });
        useAlert(this.$t('CONVERSATION.FRANCHISE_UNIT.UPDATED'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('CONVERSATION.FRANCHISE_UNIT.UPDATE_FAILED')
        );
      }
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onAssignMyTeam() {
      if (!this.primaryMyTeam) return;

      this.assignedTeam = this.primaryMyTeam;
    },

    // Cada opção carrega a cor do próprio nível (#1243). A selecionada ganha o
    // fundo tingido (-75) além do texto; as outras ficam só com o texto (-900),
    // que é o degrau com contraste de sobra nos dois temas — as quatro rampas
    // invertem sozinhas no escuro, então não há variante `dark:` aqui.
    // Só a cor do texto: o fundo do segmento escolhido é o polegar, que desliza
    // por trás de todos.
    //
    // Escolhido e não escolhido trocaram de papel com as cores cheias. Antes
    // cada rótulo vinha tingido do próprio nível, o tempo todo, porque o fundo
    // era claro e a cor era o que distinguia um do outro. Agora quem carrega a
    // cor é o polegar, e o rótulo em cima dele precisa é de CONTRASTE contra
    // ela — daí a tinta da tabela. Os outros voltam para `text-muted`, que é o
    // que a referência da #1243 faz (`PRIO_INK[i]` no escolhido, `var(--mut)`
    // nos demais): cinco rótulos tingidos ao mesmo tempo disputavam a atenção
    // com o único que estava valendo.
    // O peso segue a mesma divisão: só o escolhido é `semibold`. Antes os cinco
    // eram, e cinco rótulos em negrito lado a lado não destacam nenhum — o
    // negrito só significa alguma coisa quando os vizinhos não o têm.
    priorityOptionClasses(option) {
      if (this.assignedPriority.id !== option.id) {
        return 'font-medium text-muted hover:text-body';
      }

      const config = PRIORITY_CONFIG[option.id] || PRIORITY_NONE_CONFIG;
      return `font-semibold ${config.ink}`;
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div class="bg-transparent">
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <woot-button
            v-if="showSelfAssign"
            icon="arrow-right"
            variant="link"
            size="small"
            @click="onSelfAssign"
          >
            {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
          </woot-button>
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        class="panel-selector"
        :options="availableAgents"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @click="onClickAssignAgent"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      >
        <template #button>
          <woot-button
            v-if="primaryMyTeam && !isAssignedToMyTeam"
            icon="people-team"
            variant="link"
            size="small"
            @click="onAssignMyTeam"
          >
            {{ $t('CONTACTS_PAGE.ASSIGN_SELECTED_CONTACTS_TO_TEAM') }}
          </woot-button>
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        class="panel-selector"
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.INPUT')
        "
        @click="onClickAssignTeam"
      />
    </div>
    <div v-if="showFranchiseUnit" class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION.FRANCHISE_UNIT.TITLE')"
      />
      <MultiselectDropdown
        class="panel-selector"
        :options="franchiseUnitsList"
        :selected-item="assignedFranchiseUnit"
        :multiselector-title="$t('CONVERSATION.FRANCHISE_UNIT.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.FRANCHISE_UNIT.PLACEHOLDER')
        "
        :no-search-result="$t('CONVERSATION.FRANCHISE_UNIT.NO_RESULTS')"
        :input-placeholder="$t('CONVERSATION.FRANCHISE_UNIT.SEARCH')"
        @click="onClickAssignFranchiseUnit"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <div
        role="group"
        :aria-label="$t('CONVERSATION.PRIORITY.TITLE')"
        class="relative flex items-center justify-between p-0.5 rounded-lg bg-surface-sunken dark:bg-surface-raised border border-line"
      >
        <!-- `justify-between`: cada rotulo ocupa a largura que ele tem e a
             sobra vai para os vaos, que ficam todos do mesmo tamanho. Coluna de
             largura fixa deixava "Alta" nadando no meio de uma celula do
             tamanho de "Nenhuma".

             Com segmentos de larguras diferentes, o polegar nao tem como sair
             de uma conta de porcentagem: ele e MEDIDO do botao escolhido
             (`updatePriorityThumb`). Pintar o fundo do proprio botao nao
             animaria nada -- `background-color` troca de um lado para o outro
             sem passar pelo meio. -->
        <span
          ref="priorityTrack"
          aria-hidden="true"
          class="absolute pointer-events-none inset-0.5"
        >
          <!-- Ancorado na borda fisica ESQUERDA, que e de onde a medida sai
               (`buttonBox.left - trackBox.left`). Em fluxo ele encostava na
               borda inicial -- a direita em RTL --, e ai o deslocamento
               positivo empurrava o polegar para fora do trilho: a selecao
               sumia. -->
          <span
            v-show="priorityThumb.width"
            class="absolute inset-y-0 left-0 transition-all duration-200 ease-out rounded-md priority-thumb"
            :class="priorityThumbClasses"
            :style="priorityThumbStyle"
          />
        </span>
        <button
          v-for="option in priorityOptions"
          :key="option.id || 'none'"
          ref="priorityOptions"
          type="button"
          class="relative z-10 px-2 py-1 text-xs transition-colors duration-150 bg-transparent border border-transparent rounded-md"
          :class="priorityOptionClasses(option)"
          :aria-pressed="(assignedPriority.id === option.id).toString()"
          @click="onClickAssignPriority(option)"
        >
          {{ option.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (prefers-reduced-motion: reduce) {
  .priority-thumb {
    transition: none;
  }
}
</style>
